/**
 * Security Event Logging Utility
 *
 * SECURITY: Logs sensitive security events for audit trail and monitoring
 * - Authentication events (login, logout, token refresh)
 * - Authorization events (permission denials)
 * - Sensitive operations (delete, data export)
 * - Suspicious activity detection
 *
 * In production, these logs should be sent to a backend logging service
 * for persistent storage and analysis.
 */

/**
 * Log levels
 */
const LogLevel = {
  INFO: 'INFO',
  WARNING: 'WARNING',
  ERROR: 'ERROR',
  CRITICAL: 'CRITICAL'
};

/**
 * Event types for security audit trail
 */
const EventType = {
  LOGIN: 'LOGIN',
  LOGOUT: 'LOGOUT',
  TOKEN_REFRESH: 'TOKEN_REFRESH',
  TOKEN_EXPIRATION: 'TOKEN_EXPIRATION',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  DELETE_OPERATION: 'DELETE_OPERATION',
  DATA_EXPORT: 'DATA_EXPORT',
  SENSITIVE_DATA_ACCESS: 'SENSITIVE_DATA_ACCESS',
  FAILED_LOGIN_ATTEMPT: 'FAILED_LOGIN_ATTEMPT',
  SUSPICIOUS_ACTIVITY: 'SUSPICIOUS_ACTIVITY'
};

/**
 * Format timestamp for logs
 */
function getTimestamp() {
  return new Date().toISOString();
}

/**
 * Get user identifier from context if available
 */
function getUserId() {
  try {
    const token = sessionStorage.getItem('accessToken');
    if (token) {
      const parts = token.split('.');
      if (parts.length === 3) {
        const payload = JSON.parse(atob(parts[1]));
        return payload.sub || payload.user_id || payload.id || 'unknown';
      }
    }
  } catch (e) {
    // Fail silently
  }
  return 'unknown';
}

/**
 * Format log entry with metadata
 */
function formatLogEntry(eventType, level, message, metadata = {}) {
  return {
    timestamp: getTimestamp(),
    eventType,
    level,
    message,
    userId: getUserId(),
    userAgent: navigator.userAgent,
    url: window.location.href,
    ...metadata
  };
}

/**
 * Send log entry to backend (TODO: implement backend integration)
 * localStorage persistence has been removed — audit logs stored in localStorage are
 * readable by any XSS payload. Logs are kept in-memory (console) only until a
 * backend endpoint (POST /api/v1/audit-logs) is implemented.
 */
function sendLogToBackend(logEntry) {
  // TODO: Implement backend integration
  // POST /api/v1/audit-logs with logEntry
  // This should be batched and sent periodically or on critical events

  // Emit critical/error events to console.error so they are visible in production
  // monitoring tools that capture console output.
  if (logEntry.level === LogLevel.ERROR || logEntry.level === LogLevel.CRITICAL) {
    console.error('[SECURITY AUDIT]', logEntry);
  }
}

/**
 * Internal logging function
 */
function logSecurityEvent(eventType, level, message, metadata = {}) {
  const logEntry = formatLogEntry(eventType, level, message, metadata);

  // Log to browser console in development
  if (process.env.NODE_ENV === 'development') {
    const logFn = level === LogLevel.ERROR || level === LogLevel.CRITICAL ? console.error : console.warn;
    logFn(`[SECURITY] [${eventType}]`, message, metadata);
  }

  // Send to backend (batched in production)
  sendLogToBackend(logEntry);

  return logEntry;
}

/**
 * Log successful login
 */
export function logLogin(username) {
  return logSecurityEvent(
    EventType.LOGIN,
    LogLevel.INFO,
    `User login successful: ${username}`,
    { username }
  );
}

/**
 * Log failed login attempt
 */
export function logFailedLogin(username, reason) {
  return logSecurityEvent(
    EventType.FAILED_LOGIN_ATTEMPT,
    LogLevel.WARNING,
    `Failed login attempt: ${username} - ${reason}`,
    { username, reason }
  );
}

/**
 * Log logout
 */
export function logLogout() {
  return logSecurityEvent(
    EventType.LOGOUT,
    LogLevel.INFO,
    'User logout'
  );
}

/**
 * Log token refresh
 */
export function logTokenRefresh() {
  return logSecurityEvent(
    EventType.TOKEN_REFRESH,
    LogLevel.INFO,
    'Access token refreshed'
  );
}

/**
 * Log token expiration warning
 */
export function logTokenExpiration() {
  return logSecurityEvent(
    EventType.TOKEN_EXPIRATION,
    LogLevel.WARNING,
    'Token expiration warning shown to user (expires in 2 minutes)'
  );
}

/**
 * Log permission denial
 */
export function logPermissionDenied(action, resource, reason) {
  return logSecurityEvent(
    EventType.PERMISSION_DENIED,
    LogLevel.WARNING,
    `Permission denied: ${action} on ${resource}`,
    { action, resource, reason }
  );
}

/**
 * Log delete operation
 */
export function logDeleteOperation(resourceType, resourceId, details = {}) {
  return logSecurityEvent(
    EventType.DELETE_OPERATION,
    LogLevel.WARNING,
    `Delete operation: ${resourceType} (${resourceId})`,
    { resourceType, resourceId, ...details }
  );
}

/**
 * Log data export
 */
export function logDataExport(dataType, recordCount, filters = {}) {
  return logSecurityEvent(
    EventType.DATA_EXPORT,
    LogLevel.WARNING,
    `Data export: ${dataType} (${recordCount} records)`,
    { dataType, recordCount, filters }
  );
}

/**
 * Log sensitive data access
 */
export function logSensitiveDataAccess(dataType, resource, accessType) {
  return logSecurityEvent(
    EventType.SENSITIVE_DATA_ACCESS,
    LogLevel.INFO,
    `Sensitive data access: ${dataType} on ${resource}`,
    { dataType, resource, accessType }
  );
}

/**
 * Log suspicious activity
 */
export function logSuspiciousActivity(description, details = {}) {
  return logSecurityEvent(
    EventType.SUSPICIOUS_ACTIVITY,
    LogLevel.CRITICAL,
    `Suspicious activity detected: ${description}`,
    details
  );
}

/**
 * Retrieve stored logs (for debugging)
 * localStorage persistence was removed (XSS risk). Always returns empty array until
 * backend audit-log endpoint is implemented.
 */
export function getStoredLogs() {
  return [];
}

/**
 * Clear stored logs
 * No-op: localStorage persistence has been removed.
 */
export function clearStoredLogs() {
  // No-op — localStorage persistence removed for security.
}

export { EventType, LogLevel };
